import { ColorsConfig } from '@ddt-web/config';
import * as isNil from 'lodash/isNil';
import * as range from 'lodash/range';

import { IHighChartsAttributesModel } from '../../models/high-charts-attributes.interface';
import { BaseChartWorker } from '../common';

/**
 * ProjectedChartWorker: chart worker specific to a Line Chart.
 * @param {any} config JSON object representing the chart configuration.
 * @param {IHighChartsAttributesModel} highChartsAttributes an object containing properties used to augment the chart configuration.
 */
export class ProjectedChartWorker extends BaseChartWorker {

	constructor(config: any, highChartsAttributes: IHighChartsAttributesModel) {
		super(config, highChartsAttributes);
	}

	public transformData(): void {

		// These lines check if colours are supplied, and otherwise load a default from ColorsConfig.
		// They also add opacity values to the colours, FF (100%) for the line and 80 (50%) for the range area.
		const chartLineDefaultOpacity = 'FF';
		const chartRangeDefaultOpacity = '80';
		const colors = this.highChartsAttributes.customAttributes.colors ?? ColorsConfig.MULTI_SERIES_PORTFOLIO_COLORS[0].map((color) => color + chartLineDefaultOpacity);
		const colorTints = this.highChartsAttributes.customAttributes.colorTints ?? ColorsConfig.MULTI_SERIES_PORTFOLIO_COLORS[2].map((color) => color + chartRangeDefaultOpacity);

		const splineSeries = [];
		const rangeSeries = [];
		const portfolioTables = this.highChartsAttributes.tabData.tableData;

		const dollarAmount = this.highChartsAttributes.customAttributes['dollarAmountMultiplier'];
		const rangeAmountMultiplier = dollarAmount ?? 100;

		const startDate = new Date(this.highChartsAttributes.customAttributes['asOfDate']);
		const dates = [startDate];


		portfolioTables.forEach((portfolioTableData, portfolioIndex) => {

			const splineSeriesData = [
				{
					x: dates[0],
					y: dollarAmount ?? 0
				}
			];
			const rangeSeriesData = [
				{
					x: dates[0],
					low: dollarAmount ?? 0,
					high: dollarAmount ?? 0
				}
			];

			portfolioTableData.cells.forEach((cell, i) => {
				const period = i + 1;

				let date = dates[period];
				if (isNil(date)) {
					date = new Date(new Date(startDate).setFullYear(startDate.getFullYear() + period));
					dates[period] = date;
				}

				const medianValue = cell[0]?.rawValue as number;
				const highValue = cell[1]?.rawValue as number;
				const lowValue = cell[2]?.rawValue as number;
				if (medianValue) {
					splineSeriesData.push({
						x: date,
						y: medianValue * rangeAmountMultiplier
					});

					rangeSeriesData.push({
						x: date,
						low: lowValue ? lowValue * rangeAmountMultiplier : null,
						high: highValue ? highValue * rangeAmountMultiplier : null

					});
				}
			});


			splineSeries.push({
				id: 'spline-portfolio-' + portfolioIndex,
				name: portfolioTableData.portfolioData?.[0]?.formattedValue,
				type: 'spline',
				data: splineSeriesData,
				color: colors[portfolioIndex]
			});

			rangeSeries.push({
				id: 'range-portfolio-' + portfolioIndex,
				name: portfolioTableData.portfolioData?.[0]?.formattedValue,
				type: 'areasplinerange',
				data: rangeSeriesData,
				fillColor: colorTints[portfolioIndex],
				fillOpacity: 0,
				linkedTo: 'spline-portfolio-' + portfolioIndex
			});
		});

		this.config.series = rangeSeries.concat(splineSeries);

		// This tick positioner is a callback function that places the ticks along the axis in the HighCharts. 
		// This will get the years of the first and last dates and place ticks at steps of 'stepSize' years between those dates
		// Years can also be skipped at the start using the 'startYearOffset'.
		const startYearOffset = 1;
		const stepSize = 2;

		this.config.xAxis.tickPositioner = function (): void {
			const firstDate = dates[0];
			const firstYear = firstDate.getFullYear();
			const lastYear = dates[dates.length - 1].getFullYear();

			this.tickPositions = range(firstYear + startYearOffset, lastYear, stepSize).map((year, index) => {
				const tickDate = new Date();
				tickDate.setMonth(firstDate.getMonth(), firstDate.getDate());
				tickDate.setFullYear(year);
				tickDate.setHours(0, 0, 0, 0);
				return tickDate.getTime();
			});
		};
	}
}
